import React, { useEffect, useState, useCallback } from "react";
import { Button } from "../../../components";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../../../config/firebaseConfig";
import { collection, query, where, getDocs, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { toast } from "react-toastify";
// import StudentCard from "./StudentCard";
import ChildCard from "./ChildCard";
import SchoolLogo from "../school/SchoolLogo";
import { IoWarningOutline } from "react-icons/io5";
import { FaWhatsapp } from "react-icons/fa";
import { sendVerificationEmail } from "../dashboardFunctions";
import { LuLoader2 } from "react-icons/lu";
import DashboardInstructions from "./DashboardIns";
// import { PDFDownloadLink } from "@react-pdf/renderer";
// import PDFContentParent from "../pdf/PDFContentParent";
import { GAEventTrack } from "../../../utils/hooks/GA_events";
import StudentLoadingCard from "./StudentLoadingCard";
import { useSelector, useDispatch } from "react-redux";
import PractidoAppPaymentModule from "./PractidoAppPaymentModule";
import VerifyPhoneModal from "./VerifyPhoneModal";
import envConfig from "../../../config/envConfig";
import { GRADE_OPTIONS, AI_LAB_COURSES, TIME_SLOTS } from "../constants/ai-lab-constants";
import { updateUserDetails } from "../../../redux/features/authSlice";
import BookDemoModal from "./BookDemoModal";

import { Sparkles, ArrowRight, Calendar, Clock, Video, User, ShieldCheck, X, Mail } from "lucide-react";

// Helper to calculate the next date for a given day name (e.g. "Friday")
const getNextDateForDay = (dayName) => {
  const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const targetDay = daysOfWeek.findIndex(d => d.toLowerCase() === dayName.trim().toLowerCase());
  if (targetDay === -1) return null;

  const now = new Date();
  const currentDay = now.getDay();

  let daysToAdd = targetDay - currentDay;
  if (daysToAdd < 0) {
    daysToAdd += 7; // Target day is next week
  }

  const resultDate = new Date();
  resultDate.setDate(now.getDate() + daysToAdd);
  return resultDate;
};

// Helper to format date as "26 Jun 2026"
const formatDate = (date) => {
  if (!date) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Helper to calculate upcoming class date and start time
const getNextClassDateTime = (day, timeSlot) => {
  const nextDate = getNextDateForDay(day);
  if (!nextDate) return null;

  const parts = timeSlot.split(/\s*[-–—]\s*/);
  const startTimePart = parts[0]?.trim();
  if (!startTimePart) return nextDate;

  const match = startTimePart.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
  if (!match) return nextDate;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const ampm = match[3].toUpperCase();
  if (ampm === "PM" && hours < 12) hours += 12;
  if (ampm === "AM" && hours === 12) hours = 0;

  nextDate.setHours(hours, minutes, 0, 0);

  const now = new Date();
  if (nextDate.toDateString() === now.toDateString()) {
    const classEnd = new Date(nextDate.getTime() + 60 * 60 * 1000);
    if (now.getTime() > classEnd.getTime()) {
      nextDate.setDate(nextDate.getDate() + 7);
    }
  }

  return nextDate;
};

// Helper to check if current time is within 15 minutes before or during the class
const isClassActive = (day, timeSlot) => {
  try {
    if (!day || !timeSlot || day === "To be confirmed") return false;

    const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const now = new Date();
    const currentDayName = daysOfWeek[now.getDay()];

    if (currentDayName.toLowerCase() !== day.trim().toLowerCase()) {
      return false;
    }

    const parts = timeSlot.split(/\s*[-–—]\s*/);
    const timePart = parts[0]?.trim();
    const match = timePart.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
    if (!match) return false;

    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const ampm = match[3].toUpperCase();
    if (ampm === "PM" && hours < 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;

    const classStart = new Date();
    classStart.setHours(hours, minutes, 0, 0);

    let classEnd = new Date(classStart.getTime() + 60 * 60 * 1000);
    const endPart = parts[1]?.trim();
    const endMatch = endPart?.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
    if (endMatch) {
      let endHours = parseInt(endMatch[1], 10);
      const endMinutes = parseInt(endMatch[2], 10);
      const endAmpm = endMatch[3].toUpperCase();
      if (endAmpm === "PM" && endHours < 12) endHours += 12;
      if (endAmpm === "AM" && endHours === 12) endHours = 0;
      classEnd = new Date();
      classEnd.setHours(endHours, endMinutes, 0, 0);
    }

    const fifteenMinsMs = 15 * 60 * 1000;
    const nowTime = now.getTime();

    return nowTime >= (classStart.getTime() - fifteenMinsMs) && nowTime <= classEnd.getTime();
  } catch (e) {
    console.error("Error parsing class active state:", e);
    return false;
  }
};

const getJoinLink = (childName, courseId, day, timeSlot, batches) => {
  const matchingBatch = batches.find(b => {
    const batchTimeLabel = TIME_SLOTS.find(t => t.id === b.timeSlot)?.label || b.timeSlot;
    return b.courseId === courseId &&
      b.day === day &&
      batchTimeLabel === timeSlot;
  });

  if (matchingBatch && matchingBatch.meetLink) {
    return matchingBatch.meetLink;
  }

  // Fallback to WhatsApp
  const scheduleStr = `${day} at ${timeSlot}`;
  const message = `Hello, I'd like to join the live class for ${childName} enroled in AI Lab (${scheduleStr}).`;
  return `https://wa.me/919560734660?text=${encodeURIComponent(message)}`;
};

const ParentDashboard = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isAiLabOffer = searchParams.get("offer") === "ai-lab";
  const isBookDemoQuery = searchParams.get("type") === "book-robotics-lab-demo";
  const [showBookDemoModal, setShowBookDemoModal] = useState(isBookDemoQuery);

  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    if (currentParams.get("type") === "book-robotics-lab-demo") {
      setShowBookDemoModal(true);
    }
  }, [location.search]);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(null);
  const {
    userDetails: parentsDetails,
    loading: parentDetailsLoading,
    schoolDetails,
  } = useSelector((state) => state.auth);
  // const { students, loading: studentDetailsLoading } = useSelector(
  //   (state) => state.parentStudents
  // );
  const [dashboardChildren, setDashboardChildren] = useState([]);
  const [childrenLoading, setChildrenLoading] = useState(true);
  const [batches, setBatches] = useState([]);

  // Student Authentication States
  const [authenticatingStudent, setAuthenticatingStudent] = useState(null);
  const [studentAuthMethod, setStudentAuthMethod] = useState("otp"); // "otp" or "google"
  const [studentPhone, setStudentPhone] = useState("");
  const [studentEmail, setStudentEmail] = useState("");
  const [studentOtp, setStudentOtp] = useState("");
  const [studentOtpSent, setStudentOtpSent] = useState(false);
  const [studentLoading, setStudentLoading] = useState(false);
  const [studentError, setStudentError] = useState("");
  const [studentSuccess, setStudentSuccess] = useState(false);
  const [studentTimer, setStudentTimer] = useState(0);
  const [toastDismissed, setToastDismissed] = useState(
    sessionStorage.getItem("whatsapp_verify_dismissed") === "true"
  );
  const [showVerifyModal, setShowVerifyModal] = useState(
    () => sessionStorage.getItem("open_verify_phone_modal_on_load") === "true"
  );
  const [emailBannerDismissed, setEmailBannerDismissed] = useState(
    sessionStorage.getItem("email_verify_dismissed") === "true"
  );
  const [emailResendLoading, setEmailResendLoading] = useState(false);
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);
  const [emailSentSuccess, setEmailSentSuccess] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(
    () => auth.currentUser?.emailVerified || parentsDetails?.emailVerified || false
  );

  useEffect(() => {
    if (sessionStorage.getItem("open_verify_phone_modal_on_load") === "true") {
      sessionStorage.removeItem("open_verify_phone_modal_on_load");
    }
  }, []);

  // Timer cooldown logic for email verification resend
  useEffect(() => {
    let interval = null;
    if (emailResendCooldown > 0) {
      interval = setInterval(() => {
        setEmailResendCooldown((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [emailResendCooldown]);

  // Sync auth.currentUser emailVerified status on focus and reload
  useEffect(() => {
    const checkEmailStatus = async () => {
      if (auth.currentUser) {
        await auth.currentUser.reload().catch(() => null);
        if (auth.currentUser.emailVerified) {
          setIsEmailVerified(true);
        }
      }
    };
    checkEmailStatus();
    window.addEventListener("focus", checkEmailStatus);
    return () => window.removeEventListener("focus", checkEmailStatus);
  }, []);

  const handleSendEmailVerification = async () => {
    if (!auth.currentUser) return;
    setEmailResendLoading(true);
    try {
      const { sendEmailVerification } = await import("firebase/auth");
      await sendEmailVerification(auth.currentUser);
      setEmailSentSuccess(true);
      setEmailResendCooldown(60);
      toast.success("Verification link sent to your email!");
    } catch (err) {
      console.error("Error sending verification email:", err);
      toast.error(err?.message || "Failed to send verification email.");
    } finally {
      setEmailResendLoading(false);
    }
  };

  // Timer cooldown logic for student OTP
  useEffect(() => {
    let interval = null;
    if (studentTimer > 0) {
      interval = setInterval(() => {
        setStudentTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [studentTimer]);

  const handleSendStudentOtp = async (e) => {
    e?.preventDefault();
    if (!studentPhone || studentPhone.length !== 10) {
      setStudentError("Please enter a valid 10-digit phone number.");
      return;
    }
    setStudentLoading(true);
    setStudentError("");
    try {
      const response = await fetch(`${envConfig.backendUrl}/sendWhatsappOtpV1`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: studentPhone, purpose: "Registration" }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to send OTP.");
      }
      setStudentOtpSent(true);
      setStudentTimer(60);
    } catch (err) {
      setStudentError(err.message || "Failed to send OTP. Please try again.");
    } finally {
      setStudentLoading(false);
    }
  };

  const handleVerifyStudentOtp = async (e) => {
    e?.preventDefault();
    if (!studentOtp || studentOtp.length !== 6) {
      setStudentError("Please enter the 6-digit OTP code.");
      return;
    }
    setStudentLoading(true);
    setStudentError("");
    try {
      const response = await fetch(`${envConfig.backendUrl}/authenticateStudentWebV1`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          studentDocId: authenticatingStudent.id,
          phoneNumber: studentPhone,
          otp: studentOtp,
          email: studentEmail,
          name: authenticatingStudent.name,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Authentication failed.");
      }
      setStudentSuccess(true);
      // Update local children list so verified badge displays immediately
      setDashboardChildren(prev =>
        prev.map(c => c.id === authenticatingStudent.id ? { ...c, isAuthSetup: true, phoneNumber: studentPhone, phone: studentPhone, email: studentEmail } : c)
      );
      setTimeout(() => {
        setAuthenticatingStudent(null);
        setStudentPhone("");
        setStudentEmail("");
        setStudentOtp("");
        setStudentOtpSent(false);
        setStudentSuccess(false);
      }, 1500);
    } catch (err) {
      setStudentError(err.message || "Failed to verify student. Please try again.");
    } finally {
      setStudentLoading(false);
    }
  };

  const handleGoogleLinkStudent = async () => {
    setStudentLoading(true);
    setStudentError("");
    let tempApp = null;
    try {
      const { initializeApp } = await import("firebase/app");
      const { getAuth, signInWithPopup, GoogleAuthProvider } = await import("firebase/auth");

      const isProd = process.env.REACT_APP_URL === "https://practido.com/";
      const resolveEnvKey = (key) => {
        return process.env[`REACT_APP_FIREBASE_${key}`] ||
          process.env[`REACT_APP_FIREBASE_${isProd ? 'MOBILE' : 'WEB'}_${key}`];
      };

      // Initialize temporary app to avoid logging out the parent
      tempApp = initializeApp({
        apiKey: resolveEnvKey("API_KEY"),
        authDomain: resolveEnvKey("AUTH_DOMAIN"),
        projectId: resolveEnvKey("PROJECT_ID"),
        storageBucket: resolveEnvKey("STORAGE_BUCKET"),
        messagingSenderId: resolveEnvKey("MESSAGING_SENDER_ID"),
        appId: resolveEnvKey("APP_ID"),
        measurementId: resolveEnvKey("MEASUREMENT_ID"),
      }, "tempChildAuth");

      const tempAuth = getAuth(tempApp);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });

      const result = await signInWithPopup(tempAuth, provider);
      const childUser = result.user;

      if (!childUser.email) {
        throw new Error("Could not retrieve email from Google account.");
      }

      // Link child on backend
      const response = await fetch(`${envConfig.backendUrl}/linkStudentGoogleWebV1`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          studentDocId: authenticatingStudent.id,
          email: childUser.email,
          name: childUser.displayName || authenticatingStudent.name,
          uid: childUser.uid,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to link Google account.");
      }

      setStudentSuccess(true);
      setDashboardChildren(prev =>
        prev.map(c => c.id === authenticatingStudent.id ? { ...c, id: childUser.uid, isAuthSetup: true, email: childUser.email, authProvider: "google", phoneNumber: "", phone: "" } : c)
      );

      setTimeout(() => {
        setAuthenticatingStudent(null);
        setStudentSuccess(false);
      }, 1500);

    } catch (err) {
      setStudentError(err.message || "Google linking failed.");
    } finally {
      if (tempApp) {
        try {
          await tempApp.delete();
        } catch (e) {
          console.error("Error deleting temp app:", e);
        }
      }
      setStudentLoading(false);
    }
  };

  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const snap = await getDocs(collection(db, "ai-lab-batches"));
        setBatches(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error("Failed to fetch batches:", err);
      }
    };
    fetchBatches();
  }, []);

  // Child Class Gate State
  const [selectedGrade, setSelectedGrade] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");

  const isChild = parentsDetails?.accountType === "student";
  const showClassGate = isChild && !parentsDetails?.grade;

  useEffect(() => {
    if (isAiLabOffer) {
      navigate("/dashboard/ai-lab/enrol", { replace: true });
    }
  }, [isAiLabOffer, navigate]);

  const [activeEnrollments, setActiveEnrollments] = useState([]);
  const [pendingEnrollments, setPendingEnrollments] = useState([]);

  const fetchEnrollments = useCallback(async () => {
    const parentId = parentsDetails?.id || auth?.currentUser?.uid;
    if (!parentId) {
      setChildrenLoading(false);
      return;
    }

    if (parentsDetails?.accountType === "student") {
      // Child user: Read directly from their profile (loaded in redux)
      const aiLab = parentsDetails?.enrolment?.["ai-lab"];
      if (aiLab) {
        if (aiLab.paymentStatus === "verified") {
          setActiveEnrollments([{ id: parentId, ...aiLab }]);
          setPendingEnrollments([]);
        } else if (aiLab.paymentStatus === "pending") {
          setPendingEnrollments([{ id: parentId, ...aiLab }]);
          setActiveEnrollments([]);
        }
      } else {
        setActiveEnrollments([]);
        setPendingEnrollments([]);
      }
      setChildrenLoading(false);
      return;
    }

    // Parent user: Query children collection
    try {
      const q = query(
        collection(db, "students"),
        where("parentUid", "==", parentId)
      );
      const snap = await getDocs(q);
      const activeList = [];
      const pendingList = [];
      const childrenList = [];
      snap.docs.forEach(doc => {
        const childData = doc.data();
        childrenList.push({ id: doc.id, ...childData });
        const aiLab = childData.enrolment?.["ai-lab"];
        if (aiLab) {
          if (aiLab.paymentStatus === "verified") {
            activeList.push({ id: doc.id, ...aiLab, childName: childData.name });
          } else if (aiLab.paymentStatus === "pending") {
            pendingList.push({ id: doc.id, ...aiLab, childName: childData.name });
          }
        }
      });
      setActiveEnrollments(activeList);
      setPendingEnrollments(pendingList);
      setDashboardChildren(childrenList);
    } catch (err) {
      console.error("Error fetching AI Lab enrollments:", err);
    } finally {
      setChildrenLoading(false);
    }
  }, [parentsDetails]);

  useEffect(() => {
    fetchEnrollments();
  }, [fetchEnrollments]);

  const handleRemoveChild = async (student) => {
    if (!student?.id) return;
    const confirmDelete = window.confirm(
      `Are you sure you want to remove ${student.name}? This action cannot be undone.`
    );
    if (confirmDelete) {
      try {
        await deleteDoc(doc(db, "students", student.id));
        toast.success(`${student.name} removed successfully.`);
        fetchEnrollments();
      } catch (err) {
        console.error("Error removing student:", err);
        toast.error("Failed to remove student. Please try again.");
      }
    }
  };

  const getEnrolledClasses = () => {
    const list = [];
    dashboardChildren.forEach(child => {
      const aiLab = child.enrolment?.["ai-lab"];
      if (aiLab && aiLab.paymentStatus === "verified" && aiLab.courses) {
        Object.entries(aiLab.courses).forEach(([courseId, sched]) => {
          if (sched && sched.day && sched.timeSlot) {
            const courseInfo = AI_LAB_COURSES.find(c => c.id === courseId);
            const nextDate = getNextClassDateTime(sched.day, sched.timeSlot);
            list.push({
              childId: child.id,
              childName: child.name,
              childGrade: child.grade,
              courseId,
              courseInfo,
              day: sched.day,
              timeSlot: sched.timeSlot,
              nextDate,
              active: isClassActive(sched.day, sched.timeSlot),
              meetLink: getJoinLink(child.name, courseId, sched.day, sched.timeSlot, batches)
            });
          }
        });
      }
    });

    return list
      .filter(item => item.nextDate !== null)
      .sort((a, b) => a.nextDate.getTime() - b.nextDate.getTime());
  };

  const upcomingClasses = getEnrolledClasses();

  const handleSaveGrade = async (e) => {
    e.preventDefault();
    if (!selectedGrade) {
      setModalError("Please select a class");
      return;
    }
    setModalLoading(true);
    setModalError("");
    try {
      const uid = auth?.currentUser?.uid || parentsDetails?.uid;
      if (uid) {
        await updateDoc(doc(db, "students", uid), {
          grade: selectedGrade
        });
        dispatch(updateUserDetails({ grade: selectedGrade }));
      }
    } catch (err) {
      console.error("Error saving grade:", err);
      setModalError("Failed to save. Please try again.");
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <div className="container py-12 space-y-6">
      {/* WhatsApp Verification Toast Banner */}
      {parentsDetails && !parentsDetails.phoneVerified && !toastDismissed && (
        <div className="relative flex flex-col sm:flex-row items-center justify-between gap-4 p-4 sm:p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl shadow-sm animate-fade-in">

          {/* Close button - absolute white round dot on the corner */}
          <button
            onClick={() => {
              sessionStorage.setItem("whatsapp_verify_dismissed", "true");
              setToastDismissed(true);
            }}
            className="absolute -top-2.5 -right-2.5 bg-white border border-blue-100 hover:border-blue-200 text-slate-500 hover:text-slate-750 w-6 h-6 rounded-full flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.08)] cursor-pointer transition-all hover:scale-105"
            type="button"
            title="Dismiss verification"
          >
            <X className="w-3.5 h-3.5" />
          </button>

          <div className="flex items-center gap-3">
            <FaWhatsapp className="w-8 h-8 text-[#25D366] flex-shrink-0 animate-pulse" />
            <div>
              <p className="text-sm font-bold text-slate-800">
                Verify your WhatsApp number
              </p>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                Receive important class schedules, test reports, and exam alerts directly on your phone.
              </p>
            </div>
          </div>
          <div className="flex items-center w-full sm:w-auto justify-end">
            <button
              onClick={() => setShowVerifyModal(true)}
              className="px-5 py-2.5 bg-orange-600 hover:bg-orange-700 text-white font-extrabold text-xs rounded-xl shadow-sm transition duration-200 w-full sm:w-auto text-center"
            >
              Verify Now
            </button>
          </div>
        </div>
      )}

      {/* Email Verification Toast Banner */}
      {auth.currentUser && !isEmailVerified && !emailBannerDismissed && (
        <div className="relative flex flex-col sm:flex-row items-center justify-between gap-4 p-4 sm:p-5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100 rounded-2xl shadow-sm animate-fade-in">
          {/* Close button - absolute white round dot on the corner */}
          <button
            onClick={() => {
              sessionStorage.setItem("email_verify_dismissed", "true");
              setEmailBannerDismissed(true);
            }}
            className="absolute -top-2.5 -right-2.5 bg-white border border-amber-100 hover:border-amber-200 text-slate-500 hover:text-slate-750 w-6 h-6 rounded-full flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.08)] cursor-pointer transition-all hover:scale-105"
            type="button"
            title="Dismiss email verification"
          >
            <X className="w-3.5 h-3.5" />
          </button>

          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800">
                Verify your Email address
              </p>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                Verify <strong className="text-slate-700">{auth.currentUser.email || parentsDetails?.email}</strong> to receive exam scorecards, receipts, and account recovery access.
              </p>
            </div>
          </div>
          <div className="flex items-center w-full sm:w-auto justify-end">
            <button
              onClick={handleSendEmailVerification}
              disabled={emailResendLoading || emailResendCooldown > 0}
              className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white font-extrabold text-xs rounded-xl shadow-sm transition duration-200 w-full sm:w-auto text-center cursor-pointer"
            >
              {emailResendLoading
                ? "Sending..."
                : emailResendCooldown > 0
                ? `Resend in ${emailResendCooldown}s`
                : emailSentSuccess
                ? "✓ Link Sent"
                : "Send Verification Link"}
            </button>
          </div>
        </div>
      )}

      {/* Child Class Gate Modal */}
      {showClassGate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-xl space-y-6 text-center border border-slate-100">
            <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto">
              <Sparkles className="w-8 h-8 text-blue-600" />
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-black text-slate-800">Select Your Class/Grade</h3>
              <p className="text-sm text-slate-500 font-medium">
                Please tell us your current class to unlock your learning dashboard.
              </p>
            </div>

            <form onSubmit={handleSaveGrade} className="space-y-4 text-left">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1.5">
                  Current Class/Grade <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedGrade}
                  onChange={(e) => setSelectedGrade(e.target.value)}
                  className="w-full text-sm border border-slate-300 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none transition bg-white"
                  disabled={modalLoading}
                >
                  <option value="">Select Class</option>
                  {GRADE_OPTIONS.map((g) => (
                    <option key={g.value} value={g.value}>
                      {g.label}
                    </option>
                  ))}
                </select>
              </div>

              {modalError && (
                <p className="text-xs text-red-500 font-semibold">{modalError}</p>
              )}

              <Button
                version="secondary"
                text={modalLoading ? "Saving..." : "Save and Continue"}
                type="submit"
                loading={modalLoading}
                className="w-full py-3 rounded-xl font-bold"
              />
            </form>
          </div>
        </div>
      )}

      {/* {parentsDetails?.emailVerified === false && (
        <div className="bg-[#FFDBD2] text-[#FF0707] p-3 flex gap-2 items-center justify-center rounded-xl">
          <IoWarningOutline className="w-5 h-5 " />
          <span className="text-base">
            Please verify your email address to unlock more features and
            services. We've sent a verification link to your registered email.
          </span>
          {verificationEmailSent ? (
            <span className="font-bold">✓ Sent</span>
          ) : (
            <button
              className="font-extrabold hover:underline flex items-center gap-2"
              onClick={() => {
                sendVerificationEmail({
                  actionUrl: "dashboard",
                  setLoading: setLoading,
                  setVerificationEmailSent: setVerificationEmailSent,
                });
                //GA event tracking
                GAEventTrack({
                  event: "parent_dashboard_verif_email_sent",
                  data: {
                    uid: auth?.currentUser?.uid,
                    email: auth?.currentUser?.email,
                  },
                });
              }}
            >
              Resend Link
              {loading?.resend && (
                <span>
                  <LuLoader2 className="animate-spin" />
                </span>
              )}
            </button>
          )}
        </div>
      )} */}
      <div className="parents-dashbaord-content space-y-4">
        {parentDetailsLoading ? (
          <div className="col-span-2 flex items-center justify-center gap-5">
            <div className="bg-gray-400 w-20 h-20 overflow-hidden flex items-center justify-center rounded-full flex-shrink-0">
              <div className="w-full h-full bg-gray-400 animate-pulse flex items-center justify-center"></div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="w-48 h-8 bg-gray-300 rounded-lg animate-pulse" />
              <div className="w-32 h-4 bg-gray-300 rounded-lg animate-pulse" />
              <div className="w-40 h-4 bg-gray-300 rounded-lg animate-pulse" />
            </div>
          </div>
        ) : (
          parentsDetails?.schoolId && (
            <div>
              <SchoolLogo schoolDetails={schoolDetails} />
            </div>
          )
        )}

        <div className="space-y-6">
          {/* Pending Enrollment Alert */}
          {pendingEnrollments.length > 0 && (
            <div className="p-4 rounded-2xl border border-amber-200 bg-amber-50 text-left flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-3xs">
              <div className="space-y-1">
                <h4 className="font-extrabold text-amber-900 text-sm flex items-center gap-1.5">
                  <span className="animate-pulse">⚠️</span> Pending Enrolment Found
                </h4>
                <p className="text-xs text-amber-700 font-semibold">
                  You have a pending enrolment. Complete the payment checkout to activate your child's courses.
                </p>
              </div>
              <div className="flex-shrink-0">
                <button
                  onClick={() => navigate("/dashboard/ai-lab/enrol")}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-extrabold shadow-sm transition"
                >
                  Resume Checkout &rarr;
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4 text-left">
            <h3 className="text-2xl font-black text-slate-800">
              Live Programs
            </h3>
            <div className="relative overflow-hidden rounded-2xl bg-[#DBF2F7] border border-[#BCE3EB] p-6 sm:p-8 shadow-sm hover:shadow-md transition-all duration-300">
              {/* Decorative background blobs/glows */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/40 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-100/50 rounded-full blur-2xl -ml-16 -mb-16 pointer-events-none" />

              <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="space-y-3 max-w-3xl">
                  {/* Badge */}
                  <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-black tracking-wider uppercase shadow-sm">
                    <Sparkles className="w-3.5 h-3.5 text-amber-300 animate-pulse" />
                    <span>Practido AI LAB Program</span>
                  </div>

                  {/* Headline */}
                  <h3 className="text-xl md:text-2xl font-extrabold tracking-tight text-gray-900">
                    {activeEnrollments.length > 0
                      ? (isChild ? "Enrol in a Live Online Course" : "Enrol More Children in a Live Online Course")
                      : (isChild ? "Enrol in a Live Online Course" : "Enrol Your Child in a Live Online Course")
                    }
                  </h3>

                  {/* Subheadline */}
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed font-medium">
                    {activeEnrollments.length > 0
                      ? (isChild
                        ? "Select your course, complete payment, and we'll confirm your first class within 24 hours - directly on WhatsApp."
                        : "Add more children, select their courses, complete payment, and we’ll confirm their classes within 24 hours - directly on WhatsApp.")
                      : (isChild
                        ? "Select your course, complete payment, and we'll confirm your first class within 24 hours - directly on WhatsApp."
                        : "Select your course, complete payment, and we’ll confirm your child’s first class within 24 hours - directly on WhatsApp.")
                    }
                  </p>

                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col items-center sm:items-end md:items-center gap-2 flex-shrink-0 self-center w-full sm:w-auto">
                  <Button
                    version="secondary"
                    text="Book Free Demo"
                    handleClick={() => setShowBookDemoModal(true)}
                  >
                    <Sparkles className="w-4 h-4 ml-1 text-amber-500 animate-pulse" />
                  </Button>
                  
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/ai-lab")}
                    className="text-xs font-black text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 group transition-all"
                  >
                    Show Details
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          </div>



          <PractidoAppPaymentModule
            schoolDetails={schoolDetails}
            parentsDetails={parentsDetails}
          />
        </div>

        {!childrenLoading && dashboardChildren && dashboardChildren.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-slate-800">
              My Children
            </h3>
            <div className="flex gap-4 flex-wrap">
              {dashboardChildren.map((student) => (
                <ChildCard
                  student={student}
                  key={student.id}
                  onRemoveChild={handleRemoveChild}
                  onAuthenticate={(std) => {
                    setAuthenticatingStudent(std);
                    setStudentPhone(std.phone ? (std.phone.startsWith('91') && std.phone.length === 12 ? std.phone.substring(2) : std.phone) : "");
                    setStudentEmail(std.email || "");
                    setStudentOtp("");
                    setStudentOtpSent(false);
                    setStudentError("");
                    setStudentSuccess(false);
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {!childrenLoading && upcomingClasses.length > 0 && (
          <div className="space-y-6 pt-6 text-left">
            <div className="space-y-1">
              <h3 className="text-2xl font-black text-slate-800">
                Upcoming Events
              </h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100 w-full">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500 animate-pulse" />
                  <h4 className="font-extrabold text-slate-700 text-base">
                    AI Lab Classes
                  </h4>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/ai-lab/classes")}
                  className="text-xs font-black text-blue-700 hover:text-blue-900 flex items-center gap-1 group transition-all"
                >
                  View All
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {upcomingClasses.map((cls, idx) => {
                  const { childName, childGrade, courseInfo, day, timeSlot, nextDate, active, meetLink } = cls;

                  return (
                    <div
                      key={idx}
                      className={`bg-white border rounded-2xl px-4 py-3.5 w-full transition-all duration-300 flex flex-col justify-between min-h-[180px] ${active
                        ? "border-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.15)] ring-1 ring-emerald-500/20"
                        : "border-slate-200 shadow-sm hover:shadow-md hover:border-slate-350"
                        }`}
                    >
                      <div className="space-y-3">
                        {/* Course Name & Icon & Active Badge */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xl filter drop-shadow-xs flex-shrink-0">
                              {courseInfo?.icon || "🤖"}
                            </span>
                            <span className="font-black text-slate-900 text-base leading-tight">
                              {courseInfo?.label || "AI & Robotics"}
                            </span>
                          </div>
                          {active && (
                            <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-100 text-[10px] font-extrabold px-2 py-0.5 rounded-full flex-shrink-0">
                              <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                              </span>
                              LIVE
                            </span>
                          )}
                        </div>

                        {/* Schedule (Time Slot & Date) - Prominent */}
                        <div className="bg-slate-50 border border-slate-100 rounded-xl p-2.5 space-y-1">
                          <div className="flex items-center gap-1.5 text-blue-700 font-extrabold text-sm">
                            <Clock className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                            <span>{timeSlot}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-slate-500 font-semibold text-xs">
                            <Calendar className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            <span>{day}, {formatDate(nextDate)}</span>
                          </div>
                        </div>

                        {/* Child info (Secondary) */}
                        <div className="flex items-center gap-1.5 text-xs text-slate-600 font-medium pt-1">
                          <User className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                          <span>
                            For <strong className="font-bold text-slate-800 capitalize">{childName}</strong> ({childGrade ? (childGrade.toLowerCase().startsWith("class") ? childGrade : `Class ${childGrade}`) : "N/A"})
                          </span>
                        </div>
                      </div>

                      {/* Join Class CTA Button */}
                      <div className="pt-3 mt-auto">
                        {active ? (
                          <a
                            href={meetLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full py-2 px-4 text-white rounded-xl text-xs font-bold transition-all duration-200 flex items-center justify-center gap-2 shadow-sm hover:shadow"
                            style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
                          >
                            <Video className="w-4 h-4 animate-bounce" /> Join Live Class
                          </a>
                        ) : (
                          <button
                            disabled
                            className="w-full py-2 px-4 bg-slate-50 text-slate-400 border border-slate-200 rounded-xl text-xs font-bold flex items-center justify-center gap-2 cursor-not-allowed"
                          >
                            <Clock className="w-4 h-4 text-slate-350" /> Join active 15m before class
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* <h3>PRACTIDO Online Olympiad 2024 Participant Information</h3> */}
        {/* <div className="flex gap-4 flex-wrap">
          {parentDetailsLoading || studentDetailsLoading ? (
            <>
              <StudentLoadingCard />
              <StudentLoadingCard />
            </>
          ) : (
            students?.map((student) => (
              <StudentCard student={student} key={student.id} />
            ))
          )}
        </div> */}
        {/* <div>
          <Button
            text={`Add ${students?.length ? "More" : ""} Participants`}
            version="secondary"
            isDisabled={parentsDetails?.emailVerified === false}
            handleClick={() => {
              navigate("/onboarding/step1");
              //GA event tracking
              GAEventTrack({
                event: "add_participant_btn",
                data: {
                  uid: auth?.currentUser?.uid,
                  email: auth?.currentUser?.email,
                },
              });
            }}
          />
          {parentsDetails?.emailVerified === false && (
            <p className="text-sm text-[#FF0707] p-2">
              Verify your email id to add participants.
            </p>
          )}
        </div> */}
      </div>
      {/* <div className="dash-blue-card gap-4">
        <div className="col-md-6 w-full bg-[#DBF2F7] rounded-xl p-4 flex flex-col gap-1 justify-center">
          <h3 className="text-lg font-bold">Mark Your Calendars!</h3>
          <p className="font-medium">
            The Practido Online Olympiad exam is happening on{" "}
            <strong>[Date of Exam]</strong>.
          </p>
          <Link
            to="/examlanding"
            // target="_blank"
            className="bg-white p-2 px-4 rounded-full font-semibold w-fit hover:no-underline"
            style={{ boxShadow: "0px 3px 0px #DFDFDF" }}
          >
            Preview Exam Details
          </Link>
        </div>
        <div className="col-md-6 w-full bg-[#DBF2F7] rounded-xl p-4 flex flex-col gap-1 justify-center">
          <p className="text-base font-medium">
            Ensure a smooth Olympiad experience! Download the Information PDF
            for your child's competition details.
          </p>

          <button
            className="bg-white p-2 px-4 rounded-full font-semibold w-fit"
            style={{ boxShadow: "0px 3px 0px #DFDFDF" }}
            onClick={() => {
              //GA event tracking
              GAEventTrack({
                event: "parent_dshbrd_download_pdf",
                data: {
                  uid: auth?.currentUser?.uid,
                  email: auth?.currentUser?.email,
                },
              });
            }}
          >
            <PDFDownloadLink
              document={
                <PDFContentParent schoolDetails={schoolDetails || null} />
              }
              fileName="Practido-Olympiad-Instructions.pdf"
            >
              {({ blob, url, loading, error }) =>
                loading ? "Loading ..." : "Click here to download the PDF"
              }
            </PDFDownloadLink>
          </button>
        </div>
      </div> */}

      <>
        <DashboardInstructions placement="parent" />

        {/* Verify Phone Modal (Only shown if triggered by toast/banner) */}
        {showVerifyModal && parentsDetails && (
          <VerifyPhoneModal
            parentsDetails={parentsDetails}
            onClose={() => setShowVerifyModal(false)}
          />
        )}

        {/* Student Authentication Modal */}
        {authenticatingStudent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
            <div className="relative w-full max-w-sm bg-white rounded-2xl p-5 shadow-2xl border border-gray-100 flex flex-col gap-4 animate-scale-up">
              <button
                onClick={() => setAuthenticatingStudent(null)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-650 transition-colors font-bold text-lg"
              >
                ✕
              </button>

              {studentSuccess ? (
                <div className="flex flex-col items-center justify-center text-center py-6 gap-4 animate-fade-in">
                  <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center animate-bounce">
                    <ShieldCheck className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-extrabold text-gray-900">
                    Linked Successfully!
                  </h3>
                  <p className="text-sm text-gray-600 font-medium leading-relaxed max-w-xs">
                    {authenticatingStudent.name}'s account has been verified and set up for independent login.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex flex-col items-center text-center gap-2">
                    <div className="bg-red-50 p-3 rounded-full text-red-600 ring-4 ring-red-50/50">
                      <User className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-black text-gray-900 tracking-tight">
                      Authenticate Student
                    </h3>
                    <p className="text-xs text-gray-500 font-semibold leading-relaxed">
                      Verify student credentials to enable student login.
                    </p>
                  </div>

                  {studentError && (
                    <div className="bg-red-50 text-red-700 p-2.5 rounded-lg border border-red-100 text-xs font-semibold">
                      <span>{studentError}</span>
                    </div>
                  )}

                  {/* Tab Selector (only if OTP has not been sent yet) */}
                  {!studentOtpSent && (
                    <div className="flex bg-gray-100 p-1.5 rounded-xl gap-2 w-full text-xs font-bold text-gray-500 mb-2">
                      <button
                        type="button"
                        onClick={() => setStudentAuthMethod("otp")}
                        className={`flex-1 py-2 text-center rounded-lg transition ${studentAuthMethod === "otp"
                          ? "bg-white text-gray-900 shadow-sm"
                          : "hover:text-gray-900"
                          }`}
                        disabled={studentLoading}
                      >
                        WhatsApp OTP
                      </button>
                      <button
                        type="button"
                        onClick={() => setStudentAuthMethod("google")}
                        className={`flex-1 py-2 text-center rounded-lg transition ${studentAuthMethod === "google"
                          ? "bg-white text-gray-900 shadow-sm"
                          : "hover:text-gray-900"
                          }`}
                        disabled={studentLoading}
                      >
                        Google Link
                      </button>
                    </div>
                  )}

                  {studentAuthMethod === "otp" ? (
                    <>
                      {!studentOtpSent ? (
                        <form onSubmit={handleSendStudentOtp} className="flex flex-col gap-3">
                          <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-bold text-gray-700 tracking-wide">
                              Student Email ID <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              required
                              placeholder="eg: sam@mail.com"
                              value={studentEmail}
                              onChange={(e) => setStudentEmail(e.target.value)}
                              className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none transition bg-white"
                            />
                          </div>

                          <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-bold text-gray-700 tracking-wide">
                              Student WhatsApp Number <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-2 items-center">
                              <span className="bg-gray-100 border border-gray-200 text-gray-700 font-bold px-3 py-2.5 rounded-lg text-sm select-none">
                                +91
                              </span>
                              <input
                                type="tel"
                                required
                                maxLength={10}
                                placeholder="10-digit number"
                                value={studentPhone}
                                onChange={(e) => setStudentPhone(e.target.value.replace(/\D/g, ""))}
                                className="w-full flex-grow text-sm border border-slate-300 rounded-xl px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none transition bg-white"
                              />
                            </div>
                          </div>

                          <div className="mt-2">
                            <Button
                              version="secondary"
                              text={studentLoading ? "Sending..." : "Send OTP"}
                              type="submit"
                              loading={studentLoading}
                              isDisabled={studentPhone.length !== 10 || !studentEmail || studentLoading}
                              width="100%"
                            />
                          </div>
                        </form>
                      ) : (
                        <form onSubmit={handleVerifyStudentOtp} className="flex flex-col gap-3">
                          <div className="flex flex-col gap-2">
                            <p className="text-xs text-gray-650 font-semibold leading-relaxed">
                              Enter 6-digit OTP code sent to <strong>+91 *******{studentPhone.slice(-4)}</strong>:
                            </p>
                            <input
                              type="text"
                              required
                              maxLength={6}
                              placeholder="Enter OTP"
                              value={studentOtp}
                              onChange={(e) => setStudentOtp(e.target.value.replace(/\D/g, ""))}
                              className="w-full text-center text-lg font-bold tracking-widest border border-slate-300 rounded-xl px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none transition bg-white"
                            />
                          </div>

                          <div className="flex flex-col gap-2.5 mt-1">
                            <Button
                              version="secondary"
                              text={studentLoading ? "Verifying..." : "Verify & Complete Link"}
                              type="submit"
                              loading={studentLoading}
                              isDisabled={studentOtp.length !== 6 || studentLoading}
                              width="100%"
                            />

                            <div className="flex justify-between items-center text-[11px] mt-1">
                              <button
                                type="button"
                                onClick={() => setStudentOtpSent(false)}
                                className="text-gray-500 hover:text-gray-750 font-bold transition-colors"
                              >
                                Change Details
                              </button>

                              <div>
                                {studentTimer > 0 ? (
                                  <span className="text-gray-400 font-semibold">
                                    Resend in {studentTimer}s
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={handleSendStudentOtp}
                                    className="text-blue-600 hover:text-blue-800 font-bold transition-colors"
                                  >
                                    Resend OTP
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </form>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-col gap-4 text-center py-2 animate-fade-in">
                      <p className="text-xs text-slate-500 font-semibold leading-relaxed max-w-xs mx-auto">
                        This will open a Google Sign-In popup. Please select your child's Google account to register their email credentials.
                      </p>

                      <button
                        type="button"
                        onClick={handleGoogleLinkStudent}
                        disabled={studentLoading}
                        className="w-full py-2.5 px-4 border border-slate-300 hover:bg-slate-50 text-slate-700 font-black rounded-xl text-xs flex items-center justify-center gap-2 transition duration-200"
                      >
                        {studentLoading ? (
                          <span>Linking...</span>
                        ) : (
                          <>
                            <svg className="w-4 h-4" viewBox="0 0 24 24">
                              <path
                                fill="#4285F4"
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                              />
                              <path
                                fill="#34A853"
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                              />
                              <path
                                fill="#FBBC05"
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                              />
                              <path
                                fill="#EA4335"
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                              />
                            </svg>
                            <span>Link Google Account</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </>
      <BookDemoModal
        isOpen={showBookDemoModal}
        onClose={() => {
          setShowBookDemoModal(false);
          navigate("/dashboard", { replace: true });
        }}
        existingChildren={dashboardChildren}
        parentsDetails={parentsDetails}
        onSuccess={() => {
          setShowBookDemoModal(false);
          fetchEnrollments();
          navigate("/dashboard", { replace: true });
        }}
      />
    </div>
  );
};

export default ParentDashboard;
